/**
 * Public legal pages: /terms and /privacy.
 *
 * These are starter drafts written for an India-jurisdiction, B2C+B2B
 * education SaaS (Actanon Technologies) that processes student / teacher
 * data, payments through Razorpay, and AI generations via OpenAI, Anthropic
 * (Claude) and Google (Gemini). Please have your counsel review before
 * relying on this content for a paid customer signature.
 *
 * Both pages share the same shell so the typography stays consistent and
 * Google's verification reviewers see a single coherent legal section.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, ScrollText, Mail, BarChart3 } from 'lucide-react';
import { api } from '../services/api';

const LAST_UPDATED = 'May 21, 2026';
const COMPANY_NAME = 'Actanon Technologies';
const PRODUCT_NAME = 'Actanon.AI / Student Intelligence Platform';
const CONTACT_EMAIL = 'support@actanonedu.com';
const PRIMARY_URL = 'https://actanonedu.com';

function LegalShell({ title, icon: Icon, eyebrow, children, testid }) {
  const navigate = useNavigate();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950" data-testid={testid}>
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-slate-200 dark:border-slate-800 bg-white/85 dark:bg-slate-950/85 backdrop-blur">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            data-testid="legal-back-home"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to homepage
          </button>
          <nav className="flex items-center gap-5 text-sm font-medium text-muted-foreground">
            <Link to="/terms" className="hover:text-foreground" data-testid="legal-nav-terms">Terms</Link>
            <Link to="/privacy" className="hover:text-foreground" data-testid="legal-nav-privacy">Privacy</Link>
            <a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-foreground inline-flex items-center gap-1">
              <Mail className="h-3.5 w-3.5" />
              Contact
            </a>
          </nav>
        </div>
      </header>

      {/* Body */}
      <main className="max-w-4xl mx-auto px-6 py-12 lg:py-16">
        <div className="flex items-center gap-3 text-xs uppercase tracking-[0.18em] text-brand-deep-blue font-semibold">
          <Icon className="h-4 w-4" />
          {eyebrow}
        </div>
        <h1 className="mt-3 text-4xl sm:text-5xl font-heading font-bold text-foreground leading-tight">
          {title}
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Last updated: <span className="font-medium text-foreground">{LAST_UPDATED}</span>
        </p>

        <article
          className="mt-10 max-w-none text-foreground space-y-5
            [&_h2]:font-heading [&_h2]:font-bold [&_h2]:text-2xl [&_h2]:sm:text-3xl
            [&_h2]:text-brand-deep-blue [&_h2]:mt-12 [&_h2]:mb-3
            [&_h3]:font-heading [&_h3]:font-semibold [&_h3]:text-lg [&_h3]:mt-8 [&_h3]:mb-2
            [&_p]:leading-relaxed [&_p]:text-base [&_p]:text-slate-700 [&_p]:dark:text-slate-300
            [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1.5 [&_ul]:text-slate-700 [&_ul]:dark:text-slate-300
            [&_li]:leading-relaxed
            [&_a]:text-brand-deep-blue [&_a]:font-medium hover:[&_a]:underline
            [&_strong]:text-foreground [&_strong]:font-semibold
            [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:bg-slate-100 [&_code]:dark:bg-slate-800 [&_code]:text-[0.9em]"
        >
          {children}
        </article>

        <div className="mt-16 pt-8 border-t border-slate-200 dark:border-slate-800 text-sm text-muted-foreground">
          Questions about this document? Email{' '}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-brand-deep-blue font-medium">
            {CONTACT_EMAIL}
          </a>{' '}
          and we'll respond within 5 business days.
        </div>
      </main>
    </div>
  );
}

/* ───────────────────────────────────────────────────────────── */
/* TERMS OF SERVICE                                              */
/* ───────────────────────────────────────────────────────────── */
export function TermsPage() {
  return (
    <LegalShell
      title="Terms of Service"
      eyebrow="Legal"
      icon={ScrollText}
      testid="terms-page"
    >
      <p>
        These Terms of Service (<strong>"Terms"</strong>) govern your access to and use of {PRODUCT_NAME}{' '}
        (the <strong>"Service"</strong>) operated by {COMPANY_NAME} (<strong>"we"</strong>, <strong>"us"</strong>,{' '}
        <strong>"our"</strong>). By creating an account, signing in with Google, or otherwise using the Service,
        you agree to be bound by these Terms. If you do not agree, please do not use the Service.
      </p>

      <h2>1. Who can use the Service</h2>
      <p>
        The Service is intended for educators, school administrators, and parents/guardians of students. You
        must be at least 18 years old (or the age of majority in your jurisdiction) to create an individual
        teacher account. Student accounts are created by school administrators on behalf of enrolled students
        and are governed by the school's own consent processes with parents/guardians.
      </p>

      <h2>2. Accounts &amp; security</h2>
      <ul>
        <li>You are responsible for maintaining the confidentiality of your password and User Code.</li>
        <li>We enforce single-device sessions and may invalidate sessions when we detect suspicious activity.</li>
        <li>
          One free individual account is permitted per device / IP. Attempts to circumvent the free-tier
          limit (e.g. multiple Gmail addresses on the same browser) may result in account suspension.
        </li>
        <li>You must provide accurate information at signup and keep it up to date.</li>
      </ul>

      <h2>3. Subscriptions, billing &amp; refunds</h2>
      <p>
        Paid plans (Starter, Pro, Elite, and school plans) are billed in INR through our payment processor
        Razorpay Software Pvt. Ltd. Subscriptions auto-renew at the end of each billing period unless
        cancelled at least 24 hours before renewal. All fees are exclusive of applicable GST.
      </p>
      <h3>Refunds</h3>
      <ul>
        <li>
          Individual monthly plans: full refund within 7 days of first purchase if you have not generated
          more than 10 documents.
        </li>
        <li>Annual plans: pro-rated refund within 30 days of purchase.</li>
        <li>
          School plans: refunds are handled per the signed Master Service Agreement; absent an MSA, the
          individual annual refund policy applies.
        </li>
        <li>
          Refunds are processed back to the original payment method within 7–10 business days. Email{' '}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> with your User Code to request a refund.
        </li>
      </ul>

      <h2>4. Acceptable use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Upload content that is unlawful, obscene, defamatory, or infringes third-party rights.</li>
        <li>Attempt to reverse-engineer, scrape at scale, or otherwise abuse the Service or its AI models.</li>
        <li>Use the Service to generate plagiarised academic work submitted as your own.</li>
        <li>
          Share your account with other teachers — buy a team / school plan if multiple educators need access.
        </li>
        <li>
          Use the Service to process personal data of students without the school's and parents'/guardians'
          informed consent where the law requires it.
        </li>
      </ul>

      <h2>5. AI-generated content</h2>
      <p>
        The Service uses third-party large language models (currently OpenAI GPT, Anthropic Claude, and Google
        Gemini) to generate worksheets, question papers, lesson plans, and other educational artefacts. You
        understand that:
      </p>
      <ul>
        <li>AI output may contain factual errors and must be reviewed by a qualified educator before use in the classroom.</li>
        <li>
          We do not warrant alignment with any specific board's marking scheme; the generated content is a
          starting point, not a publishable assessment.
        </li>
        <li>
          You retain ownership of the prompts and source material you upload. We grant you a perpetual,
          royalty-free licence to use the AI output you generate within your teaching practice.
        </li>
      </ul>

      <h2>6. Intellectual property</h2>
      <p>
        The Service, its UI, the underlying engine prompts, the Smart Insights algorithms, the Teaching
        Co-Pilot logic, and all related software and trademarks are the exclusive property of{' '}
        {COMPANY_NAME}. You receive a limited, non-exclusive, non-transferable, revocable licence to use the
        Service for its intended purpose during your active subscription term.
      </p>

      <h2>7. Service availability</h2>
      <p>
        We target 99.5% monthly uptime but do not guarantee uninterrupted service. Scheduled maintenance is
        announced at least 24 hours in advance via in-app notice or email. Generation throughput depends on
        the availability of upstream AI providers; we apply rate limits per tier as described on the pricing
        page.
      </p>

      <h2>8. Termination</h2>
      <p>
        You may close your account at any time by emailing {CONTACT_EMAIL}. We may suspend or terminate your
        access for material breach of these Terms, non-payment, or activity that endangers other users or the
        platform's integrity. Upon termination, your generated documents remain downloadable for 30 days
        before being permanently deleted.
      </p>

      <h2>9. Limitation of liability</h2>
      <p>
        To the maximum extent permitted by law, {COMPANY_NAME}'s aggregate liability arising out of or
        relating to the Service shall not exceed the amount you have paid us in the 12 months immediately
        preceding the event giving rise to the claim. We shall not be liable for indirect, incidental, or
        consequential damages.
      </p>

      <h2>10. Indemnification</h2>
      <p>
        You agree to indemnify and hold harmless {COMPANY_NAME}, its officers, directors, employees, and
        agents from any claims, damages, or expenses arising out of your misuse of the Service, your
        violation of these Terms, or your violation of any third-party right.
      </p>

      <h2>11. Governing law &amp; dispute resolution</h2>
      <p>
        These Terms are governed by the laws of India. Any dispute shall first be attempted to be resolved
        amicably; failing that, the courts of New Delhi, India shall have exclusive jurisdiction.
      </p>

      <h2>12. Changes to these Terms</h2>
      <p>
        We may revise these Terms from time to time. Material changes will be notified by email (to the
        address on file) and via an in-app banner at least 14 days before they take effect. Continued use of
        the Service after the effective date constitutes acceptance of the revised Terms.
      </p>

      <h2>13. Contact</h2>
      <p>
        For questions about these Terms or any other concern, write to us at{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> or visit{' '}
        <a href={PRIMARY_URL}>{PRIMARY_URL}</a>.
      </p>
    </LegalShell>
  );
}

/* ───────────────────────────────────────────────────────────── */
/* PRIVACY POLICY                                                */
/* ───────────────────────────────────────────────────────────── */
export function PrivacyPage() {
  return (
    <LegalShell
      title="Privacy Policy"
      eyebrow="Your data"
      icon={ShieldCheck}
      testid="privacy-page"
    >
      <p>
        This Privacy Policy explains how {COMPANY_NAME} (<strong>"we"</strong>) collects, uses, stores, and
        shares information when you use {PRODUCT_NAME}. We are committed to the principles of India's Digital
        Personal Data Protection Act, 2023 (DPDP Act) and align with comparable global standards such as the
        EU GDPR where applicable.
      </p>

      <h2>1. Information we collect</h2>
      <h3>1.1 Information you provide</h3>
      <ul>
        <li>
          <strong>Account data</strong>: full name, email, phone number, school name, subjects, classes,
          experience.
        </li>
        <li>
          <strong>Authentication data</strong>: password (stored only as a bcrypt hash), Google profile
          (email, name, profile picture) if you sign in with Google.
        </li>
        <li>
          <strong>Payment data</strong>: handled by Razorpay; we do not store your card number, CVV, or UPI
          PIN. We only store the transaction ID, plan tier, and renewal status.
        </li>
        <li>
          <strong>Content you upload</strong>: source material for the Teaching Co-Pilot (PDFs, DOCX,
          images), blueprints, and any prompts you submit. Files uploaded to Co-Pilot are stored for 24 hours
          and then auto-deleted via a TTL index.
        </li>
      </ul>

      <h3>1.2 Information we collect automatically</h3>
      <ul>
        <li>
          <strong>Device &amp; network signals</strong>: a hashed device fingerprint (<code>sip_device_id</code>)
          and the public IP address at signup, used solely to prevent free-tier abuse.
        </li>
        <li>
          <strong>Usage data</strong>: generation history (which engine, task type, subject, board) for
          analytics, debugging, and the "Last 5 generations" feature.
        </li>
        <li>
          <strong>Cookies / local storage</strong>: your JWT session token, theme preference, sidebar state.
          We do not use third-party advertising cookies.
        </li>
      </ul>

      <h2>2. How we use your data</h2>
      <ul>
        <li>Provide the Service and the AI generations you request.</li>
        <li>Authenticate you and enforce single-device session policy.</li>
        <li>Prevent fraud and abuse of the free tier.</li>
        <li>Process payments and send transactional emails (verification, password reset, User Code reminders).</li>
        <li>Improve our prompts, models, and curriculum alignment based on aggregated, anonymised metrics.</li>
        <li>Comply with legal obligations and respond to lawful requests.</li>
      </ul>

      <h2>3. Sub-processors we share data with</h2>
      <p>We share the minimum data necessary with the following sub-processors:</p>
      <ul>
        <li>
          <strong>OpenAI, Anthropic (Claude), Google (Gemini)</strong> — your prompt + source material is
          transmitted at generation time to fulfil your request. We use API endpoints that, per the providers'
          published policies, do not train on customer data.
        </li>
        <li>
          <strong>Razorpay Software Pvt. Ltd.</strong> (India) — payment processing.
        </li>
        <li>
          <strong>Resend</strong> — transactional email delivery (verification, password reset, User Code
          reminders, weekly digests).
        </li>
        <li>
          <strong>MongoDB Atlas / Google Cloud (India region)</strong> — primary data storage.
        </li>
        <li>
          <strong>Google LLC</strong> — only when you choose "Continue with Google"; we receive your email,
          name, and profile picture from Google's OAuth response.
        </li>
      </ul>

      <h2>4. Data retention</h2>
      <ul>
        <li>Account data is kept while your account is active and for 90 days after closure (to allow recovery and dispute resolution).</li>
        <li>Generated documents: indefinitely (you can delete them at any time from your library).</li>
        <li>Co-Pilot file uploads: 24 hours then auto-deleted.</li>
        <li>Server access logs: 30 days, rotated automatically.</li>
        <li>Backups: 30-day rolling snapshots.</li>
      </ul>

      <h2>5. Your rights</h2>
      <p>Under the DPDP Act (and the GDPR where applicable) you have the right to:</p>
      <ul>
        <li>Access the personal data we hold about you.</li>
        <li>Request correction of inaccurate data.</li>
        <li>Request deletion of your account and associated data.</li>
        <li>Withdraw consent for non-essential processing (e.g. marketing emails).</li>
        <li>Lodge a complaint with the Data Protection Board of India.</li>
      </ul>
      <p>
        To exercise any of these rights, email{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. We will respond within 30 days.
      </p>

      <h2>6. Children's data</h2>
      <p>
        Where the Service is used in a school setting, the school is the data fiduciary for student
        information and is responsible for obtaining parental consent. We process student data strictly on
        the school's instructions. We do not knowingly collect personal data directly from children under 18
        without verifiable parental consent.
      </p>

      <h2>7. Security</h2>
      <ul>
        <li>All traffic between your browser and our servers is encrypted with TLS 1.2+.</li>
        <li>Passwords are hashed with bcrypt (cost factor 12). We never store passwords in plain text.</li>
        <li>Sessions use signed JWTs with single-device enforcement and 7-day expiry.</li>
        <li>Database access is restricted to authenticated services on a private VPC.</li>
        <li>We perform quarterly internal security reviews and patch known CVEs within 14 days.</li>
      </ul>

      <h2>8. International transfers</h2>
      <p>
        While our primary data centres are in India, some of our AI sub-processors (OpenAI, Anthropic,
        Google) process prompts in the United States. We rely on the sub-processors' enterprise-grade
        encryption and contractual safeguards (DPAs and Standard Contractual Clauses where required) for
        such transfers.
      </p>

      <h2>9. Changes to this Policy</h2>
      <p>
        We may update this Policy from time to time. Material changes are notified by email and via an
        in-app banner at least 14 days before they take effect. The "Last updated" date at the top reflects
        the most recent revision.
      </p>

      <h2>10. Contact our Grievance Officer</h2>
      <p>
        For privacy-related questions or to exercise your data rights, please contact our Grievance Officer:
      </p>
      <p>
        <strong>{COMPANY_NAME}</strong>
        <br />
        Email: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
        <br />
        Website: <a href={PRIMARY_URL}>{PRIMARY_URL}</a>
      </p>
    </LegalShell>
  );
}


/* ───────────────────────────────────────────────────────────── */
/* TRANSPARENCY (Monthly compliance summary — IT Rules 2021)     */
/* ───────────────────────────────────────────────────────────── */
const MONTH_LABEL = (key) => {
  if (!key || !/^\d{4}-(0[1-9]|1[0-2])$/.test(key)) return key;
  const [y, m] = key.split('-');
  const name = new Date(Number(y), Number(m) - 1, 1).toLocaleString('en-US', { month: 'long' });
  return `${name} ${y}`;
};

function StatCard({ label, value, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-5 py-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">{label}</div>
      <div className="text-3xl font-bold text-foreground mt-1">{value}</div>
    </div>
  );
}

export function TransparencyPage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    (async () => {
      try {
        const r = await api.request('/legal/transparency');
        setData(r);
      } catch (e) {
        setErr(e?.message || 'Could not load the transparency report.');
      }
    })();
  }, []);

  return (
    <LegalShell
      title="Transparency Report"
      eyebrow="Compliance"
      icon={BarChart3}
      testid="transparency-page"
    >
      <p>
        In line with India's <strong>Information Technology (Intermediary Guidelines and Digital Media
        Ethics Code) Rules, 2021</strong>, ActanonEdu publishes a monthly summary of intellectual-property
        notices received, actioned, and rejected through our{' '}
        <a href="mailto:legal@actanonedu.com">Notice &amp; Takedown Process</a>. We do this because the
        teachers and schools who trust us with their workflow deserve to know exactly how seriously we
        take third-party rights — and how rarely a real issue ever arises.
      </p>

      {/* ── Cumulative tiles ── */}
      {data?.totals && (
        <div
          className="not-prose grid grid-cols-2 sm:grid-cols-4 gap-3 my-7"
          data-testid="transparency-totals"
        >
          <StatCard label="Months Tracked" value={data.totals.monthsTracked} testid="stat-months" />
          <StatCard label="Notices Received" value={data.totals.noticesReceived} testid="stat-received" />
          <StatCard label="Notices Actioned" value={data.totals.noticesActioned} testid="stat-actioned" />
          <StatCard label="Counter-notices" value={data.totals.counterNoticesReceived} testid="stat-counter" />
        </div>
      )}

      {err && (
        <div className="not-prose rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {err}
        </div>
      )}

      {!data && !err && (
        <div className="not-prose text-sm text-muted-foreground py-6">Loading…</div>
      )}

      {data?.entries && data.entries.length > 0 && (
        <div className="not-prose mt-6 overflow-x-auto" data-testid="transparency-table">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="text-left border-b border-slate-200 dark:border-slate-800">
                <th className="py-2 pr-3 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Month</th>
                <th className="py-2 px-3 text-[11px] uppercase tracking-wider text-slate-500 font-semibold text-right">Received</th>
                <th className="py-2 px-3 text-[11px] uppercase tracking-wider text-slate-500 font-semibold text-right">Actioned</th>
                <th className="py-2 px-3 text-[11px] uppercase tracking-wider text-slate-500 font-semibold text-right">Rejected</th>
                <th className="py-2 px-3 text-[11px] uppercase tracking-wider text-slate-500 font-semibold text-right">Counter</th>
                <th className="py-2 pl-3 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Note</th>
              </tr>
            </thead>
            <tbody>
              {data.entries.map((e) => (
                <tr key={e.monthKey} className="border-b border-slate-100 dark:border-slate-800/60" data-testid={`row-${e.monthKey}`}>
                  <td className="py-3 pr-3 font-semibold text-foreground">{MONTH_LABEL(e.monthKey)}</td>
                  <td className="py-3 px-3 text-right tabular-nums">{e.noticesReceived}</td>
                  <td className="py-3 px-3 text-right tabular-nums text-emerald-700">{e.noticesActioned}</td>
                  <td className="py-3 px-3 text-right tabular-nums text-slate-500">{e.noticesRejected}</td>
                  <td className="py-3 px-3 text-right tabular-nums">{e.counterNoticesReceived}</td>
                  <td className="py-3 pl-3 text-xs text-slate-600 dark:text-slate-400">{e.note || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h2>What counts as a notice?</h2>
      <p>
        Any formal communication — email, written letter, or rights-management filing — alleging that
        content on ActanonEdu infringes copyright, trademark, or other intellectual-property rights,
        sent to{' '}
        <a href="mailto:legal@actanonedu.com">legal@actanonedu.com</a>{' '}
        as per our <Link to="/terms">Terms of Service</Link>. We exclude general questions, marketing
        emails, and clearly out-of-scope requests from the count to keep this report meaningful.
      </p>

      <h2>How we classify outcomes</h2>
      <ul>
        <li><strong>Actioned</strong> — Content removed, restricted, or geo-fenced after substantiation.</li>
        <li><strong>Rejected</strong> — Notice did not meet the requirements published in our Notice &amp;
          Takedown Process (e.g. missing identification of the work, no evidence of authority, vexatious).</li>
        <li><strong>Counter-notice</strong> — A user whose content was removed in error submitted a valid
          counter-notice within the 30-day window; reviewed and resolved per policy.</li>
      </ul>

      <h2>Audit trail</h2>
      <p>
        Every entry on this page is backed by a timestamped record in our internal audit log. We retain
        all takedown correspondence and supporting evidence for 36 months from the date of notice, in
        line with Indian record-keeping conventions for digital intermediaries.
      </p>

      <h2>Contact</h2>
      <p>
        <strong>Grievance Officer, Actanon Technologies</strong>
        <br />
        Email: <a href="mailto:legal@actanonedu.com">legal@actanonedu.com</a>
        <br />
        Acknowledgment within 5 business days · Substantive response within 10 business days.
      </p>
    </LegalShell>
  );
}
